/*
Copyright 2026 Chain Care.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import { logger as rootLogger } from "matrix-js-sdk/lib/logger";

import type { ConfigOptions } from "../config/ConfigOptions";
import type {
  PrenigmaCollaborationEnvelope,
  PrenigmaCollaborationRuntimeEventSink,
  PrenigmaCollaborationRuntimeEventSinkContext,
  PrenigmaCollaborationRuntimeEventSigner,
  PrenigmaCollaborationRuntimeOptions,
} from "./collaborationRuntime";
import {
  summarizePrenigmaCollaborationRuntimeConfig,
  type PrenigmaCollaborationRuntimeConfigSummary,
} from "./collaborationRuntimeConfig";
import type { PrenigmaContinuousVoiceProducerRuntimeHost } from "./continuousVoiceProducerBridge";
import type { PrenigmaStreamingVoiceEvidenceRuntimeHost } from "./streamingVoiceEvidenceBridge";
import {
  PRENIGMA_CONTINUOUS_CALL_MEDIA_EVIDENCE_SOURCE,
  type PrenigmaContinuousMediaObservationReadback,
} from "./streamingVoiceTurnEvidence";
import type { PrenigmaTtsPlaybackRuntimeHost } from "./ttsPlaybackBridge";

const logger = rootLogger.getChild("[PrenigmaCollaborationRuntimeBootstrap]");

export type PrenigmaCollaborationRuntimeBootstrapBlocker =
  | "source_event_contract_not_configured"
  | "bootstrap_injection_policy_missing"
  | "event_sink_injection_missing"
  | "event_signer_injection_missing"
  | "tenant_id_missing"
  | "product_id_missing"
  | "session_id_missing";

export interface PrenigmaCollaborationRuntimeContext {
  tenantId?: string;
  productId?: string;
  workspaceId?: string;
  projectId?: string;
  sessionId?: string;
  callId?: string;
  workflowId?: string;
  workflowRunId?: string;
  collaborationProfile?: string;
  blueprintVersion?: string;
  sourceInstanceId?: string;
  producerVersion?: string;
  classification?: string;
  consentRef?: string;
  productSessionType?: string;
}

export interface PrenigmaCollaborationRuntimeHost {
  continuousVoiceProducer?: PrenigmaContinuousVoiceProducerRuntimeHost;
  context?: PrenigmaCollaborationRuntimeContext;
  diagnostics?: {
    exposeSourceProducer?: boolean;
  };
  eventSink?: PrenigmaCollaborationRuntimeEventSink;
  eventSigner?: PrenigmaCollaborationRuntimeEventSigner;
  streamingVoiceEvidence?: PrenigmaStreamingVoiceEvidenceRuntimeHost;
  ttsPlayback?: PrenigmaTtsPlaybackRuntimeHost;
}

export interface PrenigmaCollaborationRuntimeBootstrapInputs {
  config: Pick<
    ConfigOptions,
    "prenigma_branding" | "prenigma_collaboration_runtime"
  >;
  context?: PrenigmaCollaborationRuntimeContext;
  eventSink?: PrenigmaCollaborationRuntimeEventSink;
  eventSigner?: PrenigmaCollaborationRuntimeEventSigner;
  host?: PrenigmaCollaborationRuntimeHost;
  sourceEventDeliveryObserver?: PrenigmaCollaborationRuntimeSourceEventDeliveryObserver;
}

export interface PrenigmaCollaborationRuntimeBootstrapResult {
  configured: boolean;
  ready: boolean;
  summary: PrenigmaCollaborationRuntimeConfigSummary;
  blockers: PrenigmaCollaborationRuntimeBootstrapBlocker[];
  options?: PrenigmaCollaborationRuntimeOptions;
}

export interface PrenigmaCollaborationRuntimeApiSinkOptions {
  apiPath: string;
  fetchFn?: typeof fetch;
  now?: () => number;
  sourceEventDeliveryObserver?: PrenigmaCollaborationRuntimeSourceEventDeliveryObserver;
}

export interface PrenigmaCollaborationRuntimeSourceEventDelivery {
  accepted: boolean;
  continuousMediaObservation?: PrenigmaContinuousMediaObservationReadback;
  event: PrenigmaCollaborationEnvelope;
  matrixEventIds: string[];
  matrixTimelineReadbackVerified: boolean;
  receivedAtMs: number;
  receiptEventId: string;
  responseStatus: number;
  sourceEnvelopeSignatureVerified: boolean;
  sourceEventIds: string[];
  sourceEventReadbackVerified: boolean;
}

export type PrenigmaCollaborationRuntimeSourceEventDeliveryObserver = (
  delivery: PrenigmaCollaborationRuntimeSourceEventDelivery,
) => void;

declare global {
  interface Window {
    prenigmaCollaborationRuntime?: PrenigmaCollaborationRuntimeHost;
  }
}

export function getPrenigmaCollaborationRuntimeHost():
  | PrenigmaCollaborationRuntimeHost
  | undefined {
  if (typeof window === "undefined") return undefined;
  return window.prenigmaCollaborationRuntime;
}

export function preparePrenigmaCollaborationRuntimeBootstrap({
  config,
  context,
  eventSink,
  eventSigner,
  host = getPrenigmaCollaborationRuntimeHost(),
  sourceEventDeliveryObserver,
}: PrenigmaCollaborationRuntimeBootstrapInputs): PrenigmaCollaborationRuntimeBootstrapResult {
  const summary = summarizePrenigmaCollaborationRuntimeConfig(config);
  const hostContext = host?.context;
  const configuredEventSink = createPrenigmaCollaborationRuntimeApiSink({
    apiPath: summary.eventSinkApiPath,
    sourceEventDeliveryObserver,
  });
  const resolvedEventSink = eventSink ?? host?.eventSink ?? configuredEventSink;
  const resolvedEventSigner = eventSigner ?? host?.eventSigner;
  const tenantId = firstText(context?.tenantId, hostContext?.tenantId);
  const productId = firstText(
    context?.productId,
    hostContext?.productId,
    config.prenigma_branding?.product_id,
  );
  const sessionId = firstText(context?.sessionId, hostContext?.sessionId);

  const blockers: PrenigmaCollaborationRuntimeBootstrapBlocker[] = [];
  if (!summary.sourceEventsConfigured) {
    blockers.push("source_event_contract_not_configured");
  }
  if (!summary.requiresBootstrapInjection) {
    blockers.push("bootstrap_injection_policy_missing");
  }
  if (!resolvedEventSink) blockers.push("event_sink_injection_missing");
  if (summary.sourceSigningRequired && !resolvedEventSigner) {
    blockers.push("event_signer_injection_missing");
  }
  if (!tenantId) blockers.push("tenant_id_missing");
  if (!productId) blockers.push("product_id_missing");
  if (!sessionId) blockers.push("session_id_missing");

  const configured =
    summary.sourceEventsConfigured && summary.requiresBootstrapInjection;

  if (
    blockers.length > 0 ||
    !resolvedEventSink ||
    !tenantId ||
    !productId ||
    !sessionId
  ) {
    return {
      configured,
      ready: false,
      summary,
      blockers,
    };
  }

  return {
    configured,
    ready: true,
    summary,
    blockers,
    options: withoutUndefined({
      tenantId,
      productId,
      sessionId,
      eventSink: resolvedEventSink,
      eventSigner: resolvedEventSigner,
      workspaceId: firstText(context?.workspaceId, hostContext?.workspaceId),
      projectId: firstText(context?.projectId, hostContext?.projectId),
      callId: firstText(context?.callId, hostContext?.callId),
      workflowId: firstText(context?.workflowId, hostContext?.workflowId),
      workflowRunId: firstText(
        context?.workflowRunId,
        hostContext?.workflowRunId,
      ),
      collaborationProfile: firstText(
        context?.collaborationProfile,
        hostContext?.collaborationProfile,
      ),
      blueprintVersion: firstText(
        context?.blueprintVersion,
        hostContext?.blueprintVersion,
      ),
      sourceInstanceId: firstText(
        context?.sourceInstanceId,
        hostContext?.sourceInstanceId,
      ),
      producerVersion: firstText(
        context?.producerVersion,
        hostContext?.producerVersion,
      ),
      classification: firstText(
        context?.classification,
        hostContext?.classification,
      ),
      consentRef: firstText(context?.consentRef, hostContext?.consentRef),
      productSessionType: firstText(
        context?.productSessionType,
        hostContext?.productSessionType,
      ),
      diagnostics: host?.diagnostics,
    }),
  };
}

export function createPrenigmaCollaborationRuntimeApiSink({
  apiPath,
  fetchFn = globalThis.fetch?.bind(globalThis),
  now = Date.now,
  sourceEventDeliveryObserver,
}: PrenigmaCollaborationRuntimeApiSinkOptions):
  | PrenigmaCollaborationRuntimeEventSink
  | undefined {
  const cleanApiPath = apiPath.trim();
  if (!cleanApiPath || typeof fetchFn !== "function") return undefined;

  return async (
    event: PrenigmaCollaborationEnvelope,
    context: PrenigmaCollaborationRuntimeEventSinkContext = {},
  ): Promise<void> => {
    const url = sourceEventIngestUrl(cleanApiPath, event.tenant_id);
    try {
      const response = await fetchFn(url, {
        body: JSON.stringify({
          source_event: event,
          workspace_id: event.workspace_id,
          room_id: event.room_id,
          matrix_event_ids: context.matrixEventIds ?? [],
          matrix_event_type: context.matrixEventType ?? "",
          matrix_room_id: context.matrixRoomId ?? event.room_id,
          matrix_readback_verified: context.matrixReadbackVerified === true,
        }),
        credentials: "include",
        headers: {
          "content-type": "application/json",
        },
        method: "POST",
      });
      await observeSourceEventDelivery({
        context,
        event,
        now,
        response,
        sourceEventDeliveryObserver,
      });
      if (!response.ok) {
        throw new Error(
          `source-event ingest failed with status ${response.status}`,
        );
      }
    } catch (error: unknown) {
      logger.warn("Prenigma source-event API sink delivery failed", {
        eventId: event.event_id,
        sourceEventId: event.source_event_id,
        eventType: event.event_type,
        error,
      });
      throw error;
    }
  };
}

function firstText(...values: Array<string | undefined>): string | undefined {
  return values.find((value) => value !== undefined && value.trim() !== "");
}

function sourceEventIngestUrl(apiPath: string, tenantId: string): string {
  const cleanTenantId = tenantId.trim();
  if (!cleanTenantId) return apiPath;

  try {
    if (typeof window !== "undefined") {
      const url = new URL(apiPath, window.location.origin);
      if (!url.searchParams.has("tenant_id")) {
        url.searchParams.set("tenant_id", cleanTenantId);
      }
      if (/^https?:\/\//i.test(apiPath)) return url.href;
      return `${url.pathname}${url.search}${url.hash}`;
    }
  } catch {
    return appendTenantQuery(apiPath, cleanTenantId);
  }

  return appendTenantQuery(apiPath, cleanTenantId);
}

function appendTenantQuery(apiPath: string, tenantId: string): string {
  const separator = apiPath.includes("?") ? "&" : "?";
  return `${apiPath}${separator}tenant_id=${encodeURIComponent(tenantId)}`;
}

async function observeSourceEventDelivery({
  context,
  event,
  now,
  response,
  sourceEventDeliveryObserver,
}: {
  context: PrenigmaCollaborationRuntimeEventSinkContext;
  event: PrenigmaCollaborationEnvelope;
  now: () => number;
  response: Response;
  sourceEventDeliveryObserver?: PrenigmaCollaborationRuntimeSourceEventDeliveryObserver;
}): Promise<void> {
  if (sourceEventDeliveryObserver === undefined) return;
  const parsed = await response
    .clone()
    .json()
    .catch(() => undefined);
  const body = record(parsed);
  const ingest = record(body.collaboration_runtime_source_event_ingest);
  if (Object.keys(ingest).length === 0) return;
  const timelineReadback = record(ingest.timeline_readback);
  sourceEventDeliveryObserver({
    accepted: ingest.accepted === true,
    continuousMediaObservation: continuousMediaObservationFromRecord(
      record(ingest.continuous_media_observation),
    ),
    event,
    matrixEventIds: textList(ingest.matrix_event_ids),
    matrixTimelineReadbackVerified:
      context.matrixReadbackVerified === true ||
      (timelineReadback.receipt_event_present === true &&
        timelineReadback.verified_event_present === true),
    receivedAtMs: now(),
    receiptEventId: text(ingest.receipt_event_id),
    responseStatus: response.status,
    sourceEnvelopeSignatureVerified:
      ingest.source_envelope_signature_verified === true,
    sourceEventIds: textList(ingest.source_event_ids),
    sourceEventReadbackVerified:
      ingest.live_source_event_readback_verified === true,
  });
}

function continuousMediaObservationFromRecord(
  value: Record<string, unknown>,
): PrenigmaContinuousMediaObservationReadback | undefined {
  if (Object.keys(value).length === 0) return undefined;
  if (
    text(value.evidence_source_kind) !==
      PRENIGMA_CONTINUOUS_CALL_MEDIA_EVIDENCE_SOURCE ||
    value.browser_harness !== false ||
    value.continuous_call_media_observed !== true ||
    value.derived_from_batch_voice_turn !== false ||
    value.provider_credentials_returned !== false ||
    value.proved !== true ||
    value.raw_audio_returned !== false ||
    value.raw_event_content_returned === true ||
    value.raw_secret_material_returned !== false ||
    value.raw_transcript_returned !== false ||
    value.source_envelope_signature_verified !== true ||
    value.source_event_readback_verified !== true ||
    text(value.status) !== "proved"
  ) {
    return undefined;
  }
  return {
    binding: text(value.binding),
    blockers: textList(value.blockers),
    browserHarness: false,
    continuousCallMediaObserved: true,
    derivedFromBatchVoiceTurn: false,
    evidenceSourceKind: PRENIGMA_CONTINUOUS_CALL_MEDIA_EVIDENCE_SOURCE,
    hostSlot: text(value.host_slot),
    mediaKind: text(value.media_kind),
    mediaRuntime: text(value.media_runtime),
    mode: text(value.mode),
    producer: text(value.producer),
    providerCredentialsReturned: false,
    proved: true,
    rawAudioReturned: false,
    rawEventContentReturned: false,
    rawSecretMaterialReturned: false,
    rawTranscriptReturned: false,
    receiptEventId: text(value.receipt_event_id),
    schemaVersion:
      typeof value.schema_version === "number" &&
      Number.isFinite(value.schema_version)
        ? value.schema_version
        : 1,
    sourceEnvelopeSignatureVerified: true,
    sourceEventReadbackVerified: true,
    sourceEventRef: text(value.source_event_ref),
    status: text(value.status),
    trackReadyState: text(value.track_ready_state),
    trackSource: text(value.track_source),
  };
}

function record(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function text(value: unknown): string {
  if (typeof value === "string") return value.trim();
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  return "";
}

function textList(value: unknown): string[] {
  return Array.isArray(value)
    ? value.map((item) => text(item)).filter((item) => item.length > 0)
    : [];
}

function withoutUndefined(
  options: PrenigmaCollaborationRuntimeOptions,
): PrenigmaCollaborationRuntimeOptions {
  return Object.fromEntries(
    Object.entries(options).filter(([, value]) => value !== undefined),
  ) as PrenigmaCollaborationRuntimeOptions;
}
